import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useNotification } from '../../../context/NotificationContext';
import { contentAPI } from '../../../utils/api';
import { formatImageUrl } from '../../../utils/imageUtils';
import { handleIOSPayPerUse, isIOS } from '../../../utils/iapPayment';
import appleIAP, { PRODUCT_IDS } from '../../../services/appleIAP';
import PaymentGateway from '../../../components/PaymentGateway';

const LiveClass = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showSuccess, showError } = useNotification();

  const [video, setVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applePrice, setApplePrice] = useState(null);
  const [iapFinished, setIapFinished] = useState(false);
  const [status, setStatus] = useState('loading');
  const [timeUntilStart, setTimeUntilStart] = useState(0);
  const [showRazorpay, setShowRazorpay] = useState(false);
  const iosDevice = isIOS();
  // Fallback price for web/Android (₹ base * 1.18 GST)
  const YOGA_BASE_PRICE = 299;

  useEffect(() => {
    fetchVideoDetails();
    if (iosDevice) initIAP();
    else setIapFinished(true);
  }, [id]);

  const initIAP = async () => {
    try {
      await appleIAP.initialize();
      
      const loadPrice = () => {
        const p = appleIAP.getProduct(PRODUCT_IDS.payPerUse.yoga);
        if (p) setApplePrice(p.localizedPrice);
      };
      loadPrice();
      
      const removeListener = appleIAP.on('products-updated', loadPrice);
      
      let attempts = 0;
      const pollInterval = setInterval(() => {
        attempts++;
        const hasPrice = appleIAP.getProduct(PRODUCT_IDS.payPerUse.yoga)?.localizedPrice;
        if (hasPrice || attempts >= 10) {
          clearInterval(pollInterval);
          loadPrice();
          setIapFinished(true);
          removeListener?.();
        } else {
          loadPrice();
        }
      }, 1000);

      return () => {
        clearInterval(pollInterval);
        removeListener?.();
      };
    } catch (err) {
      console.error('IAP init error:', err);
      setIapFinished(true);
    }
  };

  useEffect(() => {
    if (!video) return;

    const checkStatus = () => {
      const now = Date.now();
      const start = new Date(video.scheduled_start_time).getTime();
      const end = new Date(video.scheduled_end_time).getTime();

      if (now < start) {
        setStatus('upcoming');
        setTimeUntilStart(start - now);
      } else if (now >= start && now < end) {
        setStatus('live');
        setTimeUntilStart(0);
      } else {
        setStatus('ended');
      }
    };

    const timer = setInterval(checkStatus, 1000);
    checkStatus();

    return () => clearInterval(timer);
  }, [video]);

  const fetchVideoDetails = async () => {
    try {
      const res = await contentAPI.getYogaSessions();
      if (res.data?.success) {
        const found = res.data.data.find(v => v._id === id);
        if (found) setVideo(found);
        else showError('Video not found');
      }
    } catch (err) {
      showError('Failed to load class details');
    } finally {
      setLoading(false);
    }
  };

  const getPriceDisplay = () => {
    if (iosDevice && applePrice) return applePrice;
    return `₹${Math.round(YOGA_BASE_PRICE * 1.18)}`;
  };

  const handlePurchase = async () => {
    try {
      if (!user) return;

      // iOS: use Apple IAP
      if (iosDevice) {
        if (!applePrice && !iapFinished) {
          showError('Price is loading, please wait a moment.');
          return;
        }
        await handleIOSPayPerUse({
          productId: PRODUCT_IDS.payPerUse.yoga,
          onSuccess: async () => {
            showSuccess('Class Unlocked!');
            window.location.reload();
          },
          onError: (msg) => showError(msg),
        });
        return;
      }

      // Web/Android: use Razorpay
      setShowRazorpay(true);

    } catch (err) {
      console.error('Purchase Flow Error:', err);
      showError(err.message || 'Purchase failed');
    }
  };

  const isPurchased = user?.purchased_content?.some(p => p.content_id === id);

  const getEmbedUrl = () => {
    if (!video) return '';
    const now = Date.now();
    const start = new Date(video.scheduled_start_time).getTime();
    const offsetSeconds = Math.max(0, Math.floor((now - start) / 1000));

    let url = video.video_url;
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      const videoId = url.split('v=')[1]?.split('&')[0] || url.split('/').pop();
      return `https://www.youtube.com/embed/${videoId}?autoplay=1&start=${offsetSeconds}`;
    }
    return url;
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen bg-dark">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-500"></div>
    </div>
  );

  if (!video) return <div className="p-8 text-center bg-dark text-white min-h-screen">Class not found</div>;

  const formatTime = (ms) => {
    const hours = Math.floor(ms / 3600000);
    const minutes = Math.floor((ms % 3600000) / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const priceText = getPriceDisplay();

  return (
    <div className="min-h-screen bg-dark text-white flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-4xl bg-gray-900 rounded-2xl overflow-hidden shadow-2xl border border-white/5 backdrop-blur-xl">

        <div className="p-6 border-b border-white/10 flex justify-between items-center bg-black/20">
          <h1 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 to-amber-600">{video.title}</h1>
          <button onClick={() => navigate(-1)} className="text-gray-400 hover:text-white transition-colors flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path></svg>
            Exit
          </button>
        </div>

        <div className="aspect-video bg-black relative flex items-center justify-center overflow-hidden">
          {status === 'upcoming' && (
            <div className="text-center z-10 bg-black/60 p-8 rounded-2xl backdrop-blur-md border border-white/10 shadow-2xl">
              <h2 className="text-2xl font-bold mb-4 text-white">Class Starts In</h2>
              <div className="text-5xl font-mono text-yellow-500 mb-6 tracking-wider font-bold drop-shadow-[0_0_15px_rgba(234,179,8,0.5)]">{formatTime(timeUntilStart)}</div>
              <div className="text-gray-300 mb-8 font-medium">Please wait for the stream to begin</div>

              {!isPurchased && (
                <div className="mt-4">
                  <p className="text-gray-400 mb-4 text-sm uppercase tracking-widest">Pre-book your spot now</p>
                  <button
                    onClick={handlePurchase}
                    className="font-bold text-lg px-10 py-4 rounded-xl transition-all duration-300 shadow-xl bg-gradient-to-r from-yellow-500 to-amber-600 text-black hover:-translate-y-1 hover:shadow-[0_0_20px_rgba(234,179,8,0.4)]" 
                  >
                    {`Unlock for ${priceText}`}
                  </button>
                </div>
              )}
            </div>
          )}

          {status === 'ended' && (
            <div className="text-center z-10 bg-black/60 p-8 rounded-2xl backdrop-blur-md border border-white/10">
              <h2 className="text-3xl font-black text-yellow-400 mb-3 tracking-wide">Class Ended</h2>
              <p className="text-gray-300 font-medium">This live session has finished.</p>
            </div>
          )}

          {status === 'live' && (
            <>
              {isPurchased ? (
                <iframe
                  src={getEmbedUrl()}
                  title={video.title}
                  className="absolute inset-0 w-full h-full"
                  frameBorder="0"
                  allow="autoplay; encrypted-media"
                  allowFullScreen
                />
              ) : (
                <div className="text-center z-10 p-10 bg-black/80 rounded-2xl border border-yellow-500/30 backdrop-blur-md shadow-2xl">
                  <div className="inline-flex items-center gap-2 mb-6 px-4 py-1.5 rounded-full bg-yellow-400/20 border border-yellow-400/30 text-yellow-400 font-bold text-sm tracking-widest uppercase animate-pulse">
                    <span className="w-2 h-2 rounded-full bg-yellow-400"></span>
                    Live Now
                  </div>
                  <h2 className="text-3xl font-black text-white mb-4">Join the Session</h2>
                  <p className="mb-8 text-gray-300">Purchase this class to join the live broadcast instantly.</p>
                  <button
                    onClick={handlePurchase}
                    className="font-bold text-lg px-10 py-4 rounded-xl transition-all duration-300 shadow-xl w-full bg-gradient-to-r from-yellow-500 to-amber-600 text-black hover:-translate-y-1 hover:shadow-[0_0_20px_rgba(234,179,8,0.4)]" 
                  >
                    {`Unlock for ${priceText}`}
                  </button>
                </div>
              )}
            </>
          )}

          {(status !== 'live' || !isPurchased) && video.thumbnail_url && (
            <div className="absolute inset-0 w-full h-full opacity-30 -z-10 bg-cover bg-center transition-transform duration-[30s] hover:scale-110" style={{ backgroundImage: `url(${formatImageUrl(video.thumbnail_url)})` }}>
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/40 to-transparent"></div>
            </div>
          )}
        </div>

        <div className="p-8 bg-gradient-to-b from-gray-900 to-black">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-4">
            <div>
              <p className="text-yellow-500 font-semibold text-sm mb-2 flex items-center gap-2 uppercase tracking-widest">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>
                {new Date(video.scheduled_start_time).toLocaleString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </p>
              <p className="text-gray-300 leading-relaxed text-lg">{video.description}</p>
            </div>
            <div className="flex-shrink-0">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 text-sm text-gray-300 font-medium border border-white/10 shadow-inner">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                {video.duration} Minutes
              </div>
            </div>
          </div>
        </div>

      </div>

      {showRazorpay && (
        <PaymentGateway
          amount={YOGA_BASE_PRICE}
          planName={video?.title || 'Live Class'}
          userId={user?._id || user?.id}
          productId={`live_class_${id}`}
          role="patient"
          onSuccess={() => {
            showSuccess('Class Unlocked! Refreshing...');
            setShowRazorpay(false);
            window.location.reload();
          }}
          onError={(msg) => {
            showError(msg);
            setShowRazorpay(false);
          }}
          onClose={() => setShowRazorpay(false)}
        />
      )}
    </div>
  );
};

export default LiveClass;
